/**
 * A2UI标记解析器 (v0.9 深度适配版)
 * 解析Markdown中的A2UI标记，支持属性提取和容器包裹
 * 
 * 支持语法：
 * - :::input field="name" label="姓名":::
 * - :::choice-group field="tech" mode="multiple":::\n- A\n- B\n:::
 * - :::button action="submit" primary="true":::
 * - (旧版兼容) :::progress ...:::
 */

import React from 'react';
import { 
  ChoiceComponent, 
  ProgressComponent, 
  InputComponent, 
  TipComponent,
  ButtonComponent
} from './A2UIComponents';
import { A2UIChart } from './A2UIChart';

/**
 * 属性解析器
 * 将字符串 `field="name" primary="true"` 解析为对象 `{field: "name", primary: true}`
 */
const parseAttributes = (attrString) => {
  const attrs = {};
  if (!attrString) return attrs;

  // 匹配 key="value" 或 key='value' 或 key=“value” 或 key=value
  // 说明：LLM 偶尔会输出中文引号（“”），这里做兼容以减少解析失败概率
  const regex = /(\w+)=("([^"]*)"|'([^']*)'|“([^”]*)”|(\S+))/g;
  let match;
  while ((match = regex.exec(attrString)) !== null) {
    const key = match[1];
    // match[3]: "...", match[4]: '...', match[5]: “...”, match[6]: unquoted
    let value = match[3] ?? match[4] ?? match[5] ?? match[6];
    
    // 类型转换
    if (value === 'true') value = true;
    else if (value === 'false') value = false;
    else if (!isNaN(Number(value))) value = Number(value);
    
    attrs[key] = value;
  }
  return attrs;
};

/**
 * 选项解析器
 * 解析列表项，支持 - [Label] (value="val") 格式
 * 兼容中英文括号、引号、方括号
 */
const parseOptions = (content) => {
  return content
    .split('\n')
    // 去掉零宽字符，避免 startsWith 判断失效（某些输入法/复制粘贴会带入）
    .map(line => line.replace(/[\u200B-\u200D\uFEFF]/g, '').trim())
    // 支持更多项目符号：- * • － —
    .filter(line => /^[-*•－—]/.test(line))
    .map(line => {
      const text = line.replace(/^[-*•－—]\s*/, '').trim();
      // 支持 - Label (value="val") 或 - [Label] (value="val")
      // 兼容中英文括号 ()（）、中英文引号 ""''""
      const valueMatch = text.match(/[（(]\s*value\s*=\s*(?:[""]([^"""]*)[""]|'([^']*)'|([^)）]+))\s*[)）]\s*$/);
      if (valueMatch) {
        const value = (valueMatch[1] || valueMatch[2] || valueMatch[3] || '').trim();
        // 清理 label，同时支持中英文括号
        let label = text.replace(/\s*[（(]\s*value\s*=.*?[)）]\s*$/, '').trim();
        // 清理可能的方括号 [...] 或中文方括号 ［...］
        if ((label.startsWith('[') && label.endsWith(']')) ||
            (label.startsWith('［') && label.endsWith('］'))) {
          label = label.slice(1, -1);
        }
        // 兼容中文书名号式包裹：【...】
        if (label.startsWith('【') && label.endsWith('】')) {
          label = label.slice(1, -1);
        }
        return {
          label: label,
          value: value
        };
      }
      // 无 value 时，清理方括号
      let label = text;
      if ((label.startsWith('[') && label.endsWith(']')) ||
          (label.startsWith('［') && label.endsWith('］'))) {
        label = label.slice(1, -1);
      }
      return { label: label, value: text };
    })
    .filter(opt => opt.label);
};

export const hasA2UIMarkers = (content) => {
  if (!content || typeof content !== 'string') return false;
  return /:::(choice|progress|input|tip|button|chart)/i.test(content);
};

export const parseA2UIContent = (content) => {
  if (!content || typeof content !== 'string') {
    return { markdown: content || '', components: [] };
  }
  
  const components = [];
  // 统一换行符，解决 Windows (\r\n) / 老式 (\r) 兼容性问题
  let cleanContent = content.replace(/\r\n?/g, '\n');
  let idCounter = 0;
  
  // 1. 解析 :::input ...:::
  // 格式: :::input field="name" label="姓名":::
  // 注意：属性值中可能包含冒号（如英文 "I summarize: "），所以用 (?:"[^"]*"|[^:])+ 匹配
  cleanContent = cleanContent.replace(/:::input\s+((?:"[^"]*"|[^:])+?):::/g, (match, attrsStr) => {
    const attrs = parseAttributes(attrsStr);
    const placeholder = `<!--A2UI_INPUT_${idCounter}-->`;
    components.push({
      id: idCounter++,
      type: 'input',
      placeholder,
      props: attrs,
      element: (key) => <InputComponent key={key} {...attrs} />
    });
    return placeholder;
  });

  // 2. 解析 :::choice-group ...::: ... ::: (新版)
  // 或兼容 :::choice ... ::: (旧版)
  // 说明：choice-group 必须以独立一行的 ::: 开始，并以独立一行的 ::: 结束
  // 这样可以避免被中间的 :::tip/:::button 等误截断，也更贴近用户实际书写方式
  // 注意：结束符 ::: 后面可以紧跟 </message> 等 XML 标签，也视为合法结束
  cleanContent = cleanContent.replace(
    /(^|\n)[ \t]*:::(?:choice-group|choice)\s*(.*?)\s*\n([\s\S]*?)\n[ \t]*:::\s*(?=\n|$|<)/g,
    (match, prefix, attrsStr, body) => {
      const normalizedAttrsStr = (attrsStr || '').replace(/:::\s*$/, '').trim();
      const attrs = parseAttributes(normalizedAttrsStr);
      const options = parseOptions(body);
    
      if (options.length > 0) {
        const placeholder = `<!--A2UI_CHOICE_${idCounter}-->`;
        components.push({
          id: idCounter++,
          type: 'choice',
          placeholder,
          props: { ...attrs, options },
          element: (key) => <ChoiceComponent key={key} options={options} {...attrs} />
        });
        return `${prefix}${placeholder}`;
      }
      return match;
    }
  );

  // 3. 解析 :::button ...::: (支持多行格式和单行格式)
  // 多行格式: :::button action="xxx"\n按钮文字\n:::
  // 单行格式: :::button action="xxx" label="按钮":::
  cleanContent = cleanContent.replace(/:::button\s*(.*?)\s*\n([\s\S]*?):::/g, (match, attrsStr, body) => {
    const attrs = parseAttributes(attrsStr);
    const buttonLabel = body.trim() || attrs.label || '确认';
    const placeholder = `<!--A2UI_BUTTON_${idCounter}-->`;
    components.push({
      id: idCounter++,
      type: 'button',
      placeholder,
      props: { ...attrs, label: buttonLabel },
      element: (key) => <ButtonComponent key={key} label={buttonLabel} {...attrs} />
    });
    return placeholder;
  });
  
  // 单行格式兼容: :::button action="xxx" label="按钮":::
  cleanContent = cleanContent.replace(/:::button\s+((?:"[^"]*"|[^:\n])+?):::/g, (match, attrsStr) => {
    const attrs = parseAttributes(attrsStr);
    if (!attrs.label) return match; // 如果没有 label，跳过（应该被多行格式处理）
    const placeholder = `<!--A2UI_BUTTON_${idCounter}-->`;
    components.push({
      id: idCounter++,
      type: 'button',
      placeholder,
      props: attrs,
      element: (key) => <ButtonComponent key={key} {...attrs} />
    });
    return placeholder;
  });

  // 4. 解析 :::progress ...::: (旧版兼容 & 新版)
  cleanContent = cleanContent.replace(/:::progress\s+((?:"[^"]*"|[^:])+?):::/g, (match, args) => {
    // 尝试解析属性
    let attrs = parseAttributes(args);
    
    // 如果解析失败（旧版语法: 1/2 Label），回退到正则提取
    if (!attrs.current && !attrs.total) {
      const legacyMatch = args.match(/(\d+)\/(\d+)\s+(.+)/);
      if (legacyMatch) {
        attrs = {
          current: parseInt(legacyMatch[1], 10),
          total: parseInt(legacyMatch[2], 10),
          label: legacyMatch[3].trim()
        };
      }
    }

    const placeholder = `<!--A2UI_PROGRESS_${idCounter}-->`;
    components.push({
      id: idCounter++,
      type: 'progress',
      placeholder,
      props: attrs,
      element: (key) => <ProgressComponent key={key} {...attrs} />
    });
    return placeholder;
  });

  // 5. 解析 :::tip ...:::
  cleanContent = cleanContent.replace(/:::tip\s+(info|warning|success|error)\s*\n([\s\S]*?):::/g, (match, type, body) => {
    const placeholder = `<!--A2UI_TIP_${idCounter}-->`;
    components.push({
      id: idCounter++,
      type: 'tip',
      placeholder,
      props: { type, children: body.trim() },
      element: (key) => <TipComponent key={key} type={type}>{body.trim()}</TipComponent>
    });
    return placeholder;
  });

  // 6. 解析 :::chart type="radar" title="标题"::: ... :::
  // 格式：
  // :::chart type="radar" title="能力雷达图"
  // - 创新性: 85
  // - 可行性: 72
  // :::
  cleanContent = cleanContent.replace(
    /:::chart\s+([^\n]*?)\n([\s\S]*?):::/g,
    (match, attrsStr, body) => {
      const attrs = parseAttributes(attrsStr);
      const chartType = attrs.type || 'bar';
      const chartTitle = attrs.title || '';
      
      // 解析图表数据：支持 "- 标签: 数值" 或 "- 标签：数值" 格式
      const data = body
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.startsWith('-'))
        .map(line => {
          const text = line.replace(/^-\s*/, '');
          // 支持中英文冒号
          const colonMatch = text.match(/^(.+?)[：:]\s*(\d+(?:\.\d+)?)/);
          if (colonMatch) {
            return {
              label: colonMatch[1].trim(),
              value: parseFloat(colonMatch[2])
            };
          }
          return null;
        })
        .filter(Boolean);

      if (data.length > 0) {
        const placeholder = `<!--A2UI_CHART_${idCounter}-->`;
        components.push({
          id: idCounter++,
          type: 'chart',
          placeholder,
          props: { type: chartType, title: chartTitle, data },
          element: (key) => <A2UIChart key={key} type={chartType} title={chartTitle} data={data} />
        });
        return placeholder;
      }
      return match;
    }
  );

  return { markdown: cleanContent, components };
};

/**
 * 渲染混合内容
 * 注意：必须被 A2UIContainer 包裹才能正常工作
 */
export const renderMixedContent = (markdown, components, renderMarkdown) => {
  if (!components || components.length === 0) {
    return renderMarkdown(markdown);
  }
  
  // 🔑 判断内容是否为"有意义的"
  const hasContent = (str) => {
    if (!str) return false;
    const cleaned = str
      .replace(/<!--[\s\S]*?-->/g, '') // 移除注释
      .replace(/<[^>]*>/g, '')         // 移除HTML标签(如<br>)
      .replace(/[\s\n\r\t\u200B-\u200D\uFEFF]+/g, '') // 移除所有空白和零宽字符
      .replace(/^-{3,}$/gm, '');       // 移除Markdown水平线
    if (!cleaned) return false;
    // 额外：将“纯分隔符”视为空内容（很多模型会在按钮之间输出 --- / —— / ___ / *** / ─── 等）
    if (/^[-–—_=*·•━─]+$/.test(cleaned)) return false;
    return cleaned.length > 0;
  };

  // 1. 将内容按占位符切分
  // Split capture group: (placeholder)
  const parts = markdown.split(/(<!--A2UI_[A-Z]+_\d+-->)/);
  const result = [];
  let pendingButtons = [];

  // 辅助函数：渲染并清空待处理的按钮组
  const flushPendingButtons = () => {
    if (pendingButtons.length > 0) {
      result.push(
        <div key={`btn-group-${result.length}`} className="a2ui-btn-group">
          {pendingButtons.map(comp => (
            <React.Fragment key={`a2ui-${comp.id}`}>
              {comp.element(`comp-${comp.id}`)}
            </React.Fragment>
          ))}
        </div>
      );
      pendingButtons = [];
    }
  };

  parts.forEach((part, index) => {
    // 检查是否是占位符
    const match = part.match(/<!--A2UI_([A-Z]+)_(\d+)-->/);
    
    if (match) {
      const type = match[1]; // e.g. INPUT, BUTTON
      const id = parseInt(match[2], 10);
      const comp = components.find(c => c.id === id);
      
      if (comp) {
        if (comp.type === 'button') {
          // 如果是按钮，加入待处理队列
          pendingButtons.push(comp);
        } else {
          // 如果是其他组件，先清空之前的按钮组，再渲染当前组件
          flushPendingButtons();
          result.push(
            <div key={`a2ui-${comp.id}`} className="a2ui-component-section">
              {comp.element(`comp-${comp.id}`)}
            </div>
          );
        }
      }
    } else {
      // 是文本部分
      if (hasContent(part)) {
        // 如果有实际文本内容，先清空按钮组（说明按钮被打断了）
        flushPendingButtons();
        result.push(
          <div key={`md-${index}`} className="a2ui-markdown-section">
            {renderMarkdown(part)}
          </div>
        );
      } else {
        // 如果只是空白/换行，且正在收集按钮，则忽略这些空白（视为按钮之间的间隔）
        // 如果没有收集按钮，这些空白也会被忽略，不渲染空的 markdown section
      }
    }
  });

  // 循环结束后，清空剩余的按钮
  flushPendingButtons();
  
  return result;
};

export default {
  hasA2UIMarkers,
  parseA2UIContent,
  renderMixedContent
};
